import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const BACKEND_URL = "https://seedoza-backend1.onrender.com";
const API = `${BACKEND_URL}/api`;

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ingredients: '',
    benefits: '',
    images: '',
    variant1_size: '100g',
    variant1_price: '',
    variant1_stock: '100',
    variant2_size: '250g',
    variant2_price: '',
    variant2_stock: '100',
    variant3_size: '500g',
    variant3_price: '',
    variant3_stock: '100'
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const productData = {
      name: formData.name,
      description: formData.description,
      ingredients: formData.ingredients.split(',').map(i => i.trim()),
      benefits: formData.benefits.split(',').map(b => b.trim()),
      images: formData.images.split(',').map(img => img.trim()),
      variants: [
        { size: formData.variant1_size, price: parseFloat(formData.variant1_price), stock: parseInt(formData.variant1_stock) },
        { size: formData.variant2_size, price: parseFloat(formData.variant2_price), stock: parseInt(formData.variant2_stock) },
        { size: formData.variant3_size, price: parseFloat(formData.variant3_price), stock: parseInt(formData.variant3_stock) }
      ]
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData);
        toast.success('Product updated successfully!');
      } else {
        await axios.post(`${API}/products`, productData);
        toast.success('Product created successfully!');
      }
      setShowDialog(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      ingredients: product.ingredients.join(', '),
      benefits: product.benefits.join(', '),
      images: product.images.join(', '),
      variant1_size: product.variants[0]?.size || '100g',
      variant1_price: product.variants[0]?.price || '',
      variant1_stock: product.variants[0]?.stock || '100',
      variant2_size: product.variants[1]?.size || '250g',
      variant2_price: product.variants[1]?.price || '',
      variant2_stock: product.variants[1]?.stock || '100',
      variant3_size: product.variants[2]?.size || '500g',
      variant3_price: product.variants[2]?.price || '',
      variant3_stock: product.variants[2]?.stock || '100'
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(`${API}/products/${id}`);
        toast.success('Product deleted successfully!');
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Failed to delete product');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      ingredients: '',
      benefits: '',
      images: '',
      variant1_size: '100g',
      variant1_price: '',
      variant1_stock: '100',
      variant2_size: '250g',
      variant2_price: '',
      variant2_stock: '100',
      variant3_size: '500g',
      variant3_price: '',
      variant3_stock: '100'
    });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-products-page">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container-custom py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold">Product Management</h1>
              <p className="text-muted-foreground mt-1">Manage your product catalog</p>
            </div>
            <div className="flex space-x-4">
              <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) { setEditingProduct(null); resetForm(); } }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary flex items-center space-x-2" data-testid="add-product-button">
                    <Plus className="w-4 h-4" />
                    <span>Add Product</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
                    <div>
                      <label className="block text-sm font-medium mb-2">Product Name *</label>
                      <Input name="name" value={formData.name} onChange={handleChange} required data-testid="product-name-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Description *</label>
                      <Textarea name="description" value={formData.description} onChange={handleChange} required rows={3} data-testid="product-description-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Ingredients (comma-separated) *</label>
                      <Input name="ingredients" value={formData.ingredients} onChange={handleChange} required placeholder="Pumpkin Seeds, Sunflower Seeds, ..." data-testid="product-ingredients-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Benefits (comma-separated) *</label>
                      <Input name="benefits" value={formData.benefits} onChange={handleChange} required placeholder="High protein, No refined sugar, ..." data-testid="product-benefits-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Image URLs (comma-separated) *</label>
                      <Input name="images" value={formData.images} onChange={handleChange} required placeholder="https://..., https://..." data-testid="product-images-input" />
                    </div>

                    <div className="border-t pt-4">
                      <h3 className="font-semibold mb-3">Variants</h3>
                      <div className="space-y-4">
                        {[1, 2, 3].map((num) => (
                          <div key={num} className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-sm font-medium mb-2">Size</label>
                              <Input name={`variant${num}_size`} value={formData[`variant${num}_size`]} onChange={handleChange} required data-testid={`variant${num}-size-input`} />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Price (₹)</label>
                              <Input type="number" step="0.01" name={`variant${num}_price`} value={formData[`variant${num}_price`]} onChange={handleChange} required data-testid={`variant${num}-price-input`} />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Stock</label>
                              <Input type="number" name={`variant${num}_stock`} value={formData[`variant${num}_stock`]} onChange={handleChange} required data-testid={`variant${num}-stock-input`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex space-x-4 pt-4">
                      <Button type="submit" className="btn-primary" data-testid="save-product-button">
                        {editingProduct ? 'Update Product' : 'Create Product'}
                      </Button>
                      <Button type="button" onClick={() => { setShowDialog(false); setEditingProduct(null); resetForm(); }} className="btn-outline">
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <Link to="/admin">
                <button className="btn-outline flex items-center space-x-2" data-testid="back-to-dashboard-button">
                  <ArrowLeft className="w-4 h-4" />
                  <span>Dashboard</span>
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="container-custom py-12">
        {products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <div key={product.id} className="bg-white rounded-2xl shadow-card overflow-hidden" data-testid={`admin-product-${product.id}`}>
                <img src={product.images[0]} alt={product.name} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-2">{product.name}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{product.description}</p>
                  <div className="space-y-2 mb-4">
                    {product.variants.map((variant, idx) => (
                      <div key={idx} className="text-sm flex justify-between">
                        <span>{variant.size}</span>
                        <span className="font-semibold">₹{variant.price}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => handleEdit(product)} className="flex-1 flex items-center justify-center space-x-2 bg-primary text-white py-2 rounded-lg hover:bg-primary/90 transition-colors" data-testid={`edit-product-${product.id}`}>
                      <Edit className="w-4 h-4" />
                      <span>Edit</span>
                    </button>
                    <button onClick={() => handleDelete(product.id)} className="flex-1 flex items-center justify-center space-x-2 bg-destructive text-white py-2 rounded-lg hover:bg-destructive/90 transition-colors" data-testid={`delete-product-${product.id}`}>
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-lg text-muted-foreground">No products yet. Add your first product!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProducts;
